import Input from "../../../../components/Input/Input"
import Button from "../../../../components/Button/Button"
import { useDispatch, useSelector } from "react-redux"
import React, { useEffect, useRef, useState } from "react"
import { showSnackBar } from "../../../../components/Snackbar/Snackbar"
import { editNewLeadDetails, getLeadDetails, ssnNumberResponse } from "../../../../features/contacts/contacts"
import { clearNumber, generateSSNumber } from "../../../../utils/payment"
import CreditTables from "./CreditTables"
import { useMyContextLoading } from "../../../../components/LoadingContextProvider/LoadingContextProvider"
import { getCreditProfilesByUser, getCreditProfilesWithCooldown, getHouseholdCoverage, getLastCreditPull, getLatestCreditReportByUserId, getMergedCreditProfiles, pullCreditProfiles, refetchCreditReport } from "../../../../features/creditReports/creditReports"
import HouseholdCoverage from "../../../../components/CreditReports/HouseholdCoverage"
import { AVAILABLE_PROFILE_STATUS } from "../../../../config/consts"
import { pruneSelectionToHousehold, reconcileSelectionToProfiles } from "./helper"
import { selectFeatureFlag } from "../../../../features/featureFlags/selectors"
import usePinnedFeatureFlag from "../../../../hooks/usePinnedFeatureFlag"
import { CREDIT_PULL_SSN_GUARDS, CREDIT_STEP_NO_AUTO_REPULL, LEAD_DETAILS_SELF_HEAL } from "../../../../config/featureFlagKeys"

const CO_APP_NO_SSN = 'No full SSN on file for the co-applicant. Add the 9-digit SSN in Basic Info, then pull again. If an SSN is already on file, ask an admin to correct it.'

const lacksSsn = (subjects, personId) =>
  Boolean(subjects.find(subject => subject.userId === personId)?.missingIdentity?.includes('ssNumber'))

export const CreditSelect = ({
  setSsnNumber,
  ssnNumber,
  hasSsnOnFile,
  setDisableTabs,
  setSelectedTab,
  getUserById,
  userId,
  selectedCreditLines,
  setSelectedCreditLines,
  isCreditCheck,
  setIsCreditCheck,
  popupOnRequest = false,
  coApplicantId,
  hasCoApplicant,
  onCreditSelectSaved,
  debtSummary = false,
}) => {
  const { triggerLoading } = useMyContextLoading()

  const dispatch = useDispatch()
  // Pinned at mount: useFeatureFlagsRefresh re-fetches on window focus, so a live read would flip
  // this step between the old and new path mid-edit.
  const maskedSsnDebtsEnabled = useSelector(selectFeatureFlag('CREDIT_SELECT_MASKED_SSN_DEBTS'))
  const maskedSsnDebtsRef = useRef(maskedSsnDebtsEnabled)
  const leadDetailsSelfHeal = useSelector(selectFeatureFlag(LEAD_DETAILS_SELF_HEAL))
  const leadDetailsSelfHealRef = useRef(leadDetailsSelfHeal)
  const { enabled: noAutoRepull, pin: pinNoAutoRepull } = usePinnedFeatureFlag(CREDIT_STEP_NO_AUTO_REPULL)
  const { enabled: ssnGuards, pin: pinSsnGuards } = usePinnedFeatureFlag(CREDIT_PULL_SSN_GUARDS)
  const pullOnNextRefreshRef = useRef(false)
  const canLoadDebts = maskedSsnDebtsRef.current ? (ssnNumber || hasSsnOnFile) : ssnNumber
  const [reqError, setReqError] = useState('')
  const [tempSsn, setTempSsn] = useState(ssnNumber || '')
  const [debts, setDebts] = useState([])
  const [debtsLoaded, setDebtsLoaded] = useState(false)
  const [reportId, setReportId] = useState('')
  const [creditScore, setCreditScore] = useState(null)
  const [fileResultStatus, setFileResultStatus] = useState(null)
  const [lastPull, setLastPull] = useState(null)
  const [coAppFileResultStatus, setCoAppFileResultStatus] = useState(null)
  const [coAppPullError, setCoAppPullError] = useState('')
  const [coAppSsnMissing, setCoAppSsnMissing] = useState(false)
  const [coverageError, setCoverageError] = useState('')
  const [coverageSubjects, setCoverageSubjects] = useState([])
  const [triggerFetchDebts, setTriggerFetchDebts] = useState(false)
  const [creditSelectType, setCreditSelectType] = useState('')
  const [mergedDebts, setMergedDebts] = useState([])
  const [mergedView, setMergedView] = useState(false)

  useEffect(() => {
    const handleReqError = () => {
      if (reqError.length > 0) {
        showSnackBar("error", reqError?.replace("RequestError: ", "").trim())
        setReqError('')
      }
    }

    handleReqError()
  }, [reqError])

  const handleSsnNumber = async () => {
    pinNoAutoRepull()
    const newSSN = tempSsn?.replace(/\D/g, "")
    if (newSSN !== ssnNumber) {
      triggerLoading(true)
      const response = await dispatch(ssnNumberResponse({ userId: getUserById, ssNumber: newSSN }))
      if (response.error) {
        setReqError(response.payload ?? '')
        triggerLoading(false)
        return false
      } else {
        setSsnNumber(newSSN)
        showSnackBar("success", "Social security number successfully added")
        if (!noAutoRepull) setTriggerFetchDebts(!triggerFetchDebts)
        triggerLoading(false)
        return true
      }
    }
    return true
  }

  const handleInputChange = (value) => {
    setTempSsn(generateSSNumber(value))
  }

  const typedSsnDiffersFromKnown = () => clearNumber(tempSsn) !== clearNumber(ssnNumber || '')

  const applyCreditProfiles = (creditProfiles) => {
    if (creditProfiles.length > 0) {
      setReportId(creditProfiles[0].creditReport?._id || creditProfiles[0].creditReport)
      const score = creditProfiles[0].creditReport?.creditScore
      if (score) setCreditScore(score)
    }
    setDebts(creditProfiles.map((item, index) => ({ ...item, id: index })))
    setSelectedCreditLines(prev =>
      reconcileSelectionToProfiles({ selectedCreditLines: prev, creditProfiles, hasCoApplicant }))
    setDebtsLoaded(true)
  }

  const getCreditProfiles = async () => {
    try {
      const response = await dispatch(noAutoRepull ? getCreditProfilesWithCooldown(userId) : getCreditProfilesByUser(userId))
      if (response.error) {
        if (response?.payload?.includes('EAI_AGAIN')) {
          setReqError('Something Went Wrong!')
        } else {
          // A rejected thunk can resolve with payload === undefined: its own catch block
          // reads error.response?.data?.error.errors[0].message, which throws internally
          // whenever the error body isn't that exact shape. undefined then reaches the
          // [reqError] effect, where `reqError.length` crashes the component.
          setReqError(response?.payload ?? '')
        }
      } else {
        applyCreditProfiles(response?.payload?.data || [])
      }
    } catch (error) {
      console.error('Error fetching credit reports:', error)
    }
  }

  const pullPrimaryProfiles = async () => {
    try {
      const primaryPull = await dispatch(pullCreditProfiles(userId))
      if (pullCreditProfiles.rejected.match(primaryPull)) {
        setReqError(primaryPull.payload || 'Failed to pull the credit report')
      } else {
        applyCreditProfiles(primaryPull.payload?.data || [])
      }
    } catch (error) {
      console.error('Error pulling the credit report:', error)
    }
  }

  const getCreditSelectTypeAndCreditReport = async () => {
    try {
      const response = await dispatch(getLeadDetails(userId))
      if (response.error) {
        // Same undefined-payload hazard as getCreditProfiles above.
        setReqError(response.payload ?? '')
      } else {
        const data = response.payload && response.payload.data
        setReportId(prevState => data?.creditSelect?.creditReport?._id || prevState)
        if (data) {
          const storedType = data?.creditSelect?.creditType
          if (storedType === 'softCreditCheck') {
            setIsCreditCheck(true)
            setCreditSelectType('softCreditCheck')
          } else if (popupOnRequest && storedType !== 'creditReportManually') {
            setIsCreditCheck(null)
            setCreditSelectType('creditReportManually')
          } else {
            setIsCreditCheck(false)
            setCreditSelectType('creditReportManually')
          }
        }
      }
      const latest = await dispatch(getLatestCreditReportByUserId({ userId }))
      setCreditScore(latest?.payload?.data?.creditScore || null)
      setFileResultStatus(latest?.payload?.data?.fileResultStatus || null)
    } catch (error) {
      console.error(error)
      setReqError(error.message || 'Failed to get customer data')
    }
  }

  const updateNewLeadCreditType = async ({ type }) => {
    const postData = {
      creditSelect: {
        creditType: type === 'softCreditCheck' ? type : 'creditReportManually',
      }
    }

    if (leadDetailsSelfHealRef.current) {
      const response = await dispatch(editNewLeadDetails({ userId, data: postData }))
      if (response.error) {
        setReqError(response.payload || 'Failed to save the credit type')
        return false
      }
      applyCreditType(type)
      showSnackBar("success", "Contact successfully updated")
      return true
    }

    applyCreditType(type)

    try {
      const response = await dispatch(editNewLeadDetails({ userId, data: postData }))
      if (!response.error) {
        showSnackBar("success", "Contact successfully updated")
      }
    } catch (error) {
    }
    return true
  }

  const applyCreditType = (type) => {
    if (type === 'softCreditCheck') {
      setIsCreditCheck(true)
      setCreditSelectType('softCreditCheck')
    } else if (type === 'creditReportManually') {
      setIsCreditCheck(false)
      setCreditSelectType('creditReportManually')
    }
  }

  const loadLastPull = async () => {
    const res = await dispatch(getLastCreditPull(userId))
    if (getLastCreditPull.rejected.match(res)) {
      setLastPull(null)
      return
    }
    setLastPull(res.payload?.data || null)
  }

  const loadCoverage = async () => {
    if (!userId) return
    const res = await dispatch(getHouseholdCoverage(userId))
    if (getHouseholdCoverage.rejected.match(res)) {
      setCoverageSubjects([])
      setCoverageError(res.payload || 'Failed to read household credit coverage')
      return
    }
    setCoverageError('')
    setCoverageSubjects(res.payload?.subjects || [])
  }

  // Reads status only — this used to pull the co-applicant's credit whenever no report existed,
  // so opening the step issued a bureau request nobody asked for.
  const fetchCoAppFileResultStatus = async () => {
    if (!hasCoApplicant || !coApplicantId) {
      setCoAppFileResultStatus(null)
      return
    }
    const res = await dispatch(getLatestCreditReportByUserId({ userId: coApplicantId }))
    if (getLatestCreditReportByUserId.rejected.match(res)) {
      setCoAppFileResultStatus(null)
      return
    }
    setCoAppFileResultStatus(res?.payload?.data?.fileResultStatus || null)
  }

  const fetch = async ({ refetchCoApp = false, pullPrimary = false } = {}) => {
    pinNoAutoRepull()
    pinSsnGuards()
    triggerLoading(true)
    if (hasCoApplicant && coApplicantId && refetchCoApp && ssnGuards && lacksSsn(coverageSubjects, coApplicantId)) {
      setCoAppPullError(CO_APP_NO_SSN)
      setCoAppSsnMissing(true)
    } else if (hasCoApplicant && coApplicantId && refetchCoApp) {
      setCoAppSsnMissing(false)
      const pull = await dispatch(refetchCreditReport(coApplicantId))
      if (refetchCreditReport.rejected.match(pull)) {
        setCoAppPullError(pull.payload || 'Failed to re-fetch the co-applicant credit report')
      } else {
        setCoAppPullError('')
        setSelectedCreditLines([])
        setMergedDebts([])
        setMergedView(false)
      }
    }
    if (pullPrimary) {
      await pullPrimaryProfiles()
    } else {
      await getCreditProfiles()
    }
    await getCreditSelectTypeAndCreditReport()
    if (noAutoRepull) await loadLastPull()
    await fetchCoAppFileResultStatus()
    await loadCoverage()
    triggerLoading(false)
  }

  // Merged View renders its own `mergedDebts` slice, which the main fetch() /
  // triggerFetchDebts refresh cycle never updates. Without re-pulling it, deleting or
  // editing a debt while in Merged View leaves the stale row on screen even though the
  // API call succeeded (task 423790: "Add Contact flow: Credit line can't be deleted").
  const loadMergedDebts = async () => {
    const res = await dispatch(getMergedCreditProfiles(userId))
    // A rejected thunk's payload is a plain string (normalizeError), so a loose
    // truthiness guard would call .map on a string and throw — silently leaving the
    // merged slice stale. Only map a genuine fulfilled array; surface failures.
    if (getMergedCreditProfiles.fulfilled.match(res) && Array.isArray(res.payload)) {
      setMergedDebts(res.payload.map((item, index) => ({ ...item, id: index })))
      setSelectedCreditLines(prev => {
        const kept = pruneSelectionToHousehold({ selectedCreditLines: prev, mergedDebts: res.payload })
        return debtSummary ? reconcileSelectionToProfiles({ selectedCreditLines: kept, creditProfiles: res.payload, hasCoApplicant }) : kept
      })
    } else {
      showSnackBar('error', res?.payload || 'Failed to load merged credit profiles')
    }
  }

  const refreshAfterSave = async () => {
    await fetch()
    if (mergedView) await loadMergedDebts()
  }

  useEffect(() => {
    const refreshDebts = async () => {
      const pullPrimary = pullOnNextRefreshRef.current
      pullOnNextRefreshRef.current = false
      if (!(userId && canLoadDebts)) return
      await fetch({ pullPrimary })
      // Keep the merged slice in sync so a delete/edit reflects in Merged View too.
      if (mergedView) await loadMergedDebts()
    }
    refreshDebts()
  }, [triggerFetchDebts])

  useEffect(() => {
    const loadMergedView = async () => {
      if (hasCoApplicant && coApplicantId && debtsLoaded && !mergedView) {
        setMergedView(true)
        triggerLoading(true)
        await loadMergedDebts()
        triggerLoading(false)
      }
    }
    loadMergedView()
  }, [debtsLoaded, hasCoApplicant, coApplicantId])

  const eligibleAccounts = debts?.filter(debt => AVAILABLE_PROFILE_STATUS.includes(debt?.accountStatus?.code) && debt?.isOpenAccount)
  const ineligibleAccounts = debts?.filter(debt => !AVAILABLE_PROFILE_STATUS.includes(debt?.accountStatus?.code) || !debt?.isOpenAccount)
  return (
    <>
      {
        creditSelectType || debts.length > 0 ?
          <div className='w-full'>
            <div className="flex justify-between mb-4">
              <Button
                onClick={() => {
                  setDebts([])
                  setDebtsLoaded(false)
                  setCreditSelectType('')
                  setIsCreditCheck(null)
                }}
                size="sm"
                type="outlined"
              >
                <span>Back</span>
              </Button>
              <div>
                {hasCoApplicant && coApplicantId && debtsLoaded && (
                  <div className="flex gap-4">
                    <Button
                      size="sm"
                      type={!mergedView ? "contained" : "outlined"}
                      onClick={() => setMergedView(false)}
                    >
                      Applicant
                    </Button>
                    <Button
                      size="sm"
                      type={mergedView ? "contained" : "outlined"}
                      onClick={async () => {
                        setMergedView(true)
                        triggerLoading(true)
                        await loadMergedDebts()
                        triggerLoading(false)
                      }}
                    >
                      Merged View
                    </Button>
                  </div>
                )}
              </div>
            </div>

            <HouseholdCoverage
              subjects={coverageSubjects}
              error={coverageError}
              notice={coAppPullError}
              action={coAppSsnMissing ? { label: 'Add SSN', onClick: () => setSelectedTab('basic-info') } : undefined}
            />

            <CreditTables userId={userId} setDisableTabs={setDisableTabs} setSelectedTab={setSelectedTab}
              debts={mergedView && mergedDebts.length > 0 ? mergedDebts : debts} debtsLoaded={debtsLoaded}
              eligibleAccounts={mergedView && mergedDebts.length > 0 ? mergedDebts.filter(d => AVAILABLE_PROFILE_STATUS.includes(d?.accountStatus?.code) && d?.isOpenAccount) : eligibleAccounts}
              ineligibleAccounts={mergedView && mergedDebts.length > 0 ? mergedDebts.filter(d => !AVAILABLE_PROFILE_STATUS.includes(d?.accountStatus?.code) || !d?.isOpenAccount) : ineligibleAccounts}
              setDebts={setDebts}
              selectedCreditLines={selectedCreditLines}
              setSelectedCreditLines={setSelectedCreditLines}
              isCreditCheck={isCreditCheck}
              popupOnRequest={popupOnRequest}
              setTriggerFetchDebts={setTriggerFetchDebts}
              triggerFetchDebts={triggerFetchDebts} reportId={reportId} setReportId={setReportId}
              creditSelectType={creditSelectType} setCreditSelectType={setCreditSelectType}
              setIsCreditCheck={setIsCreditCheck}
              fetchCreditReport={noAutoRepull ? () => fetch({ refetchCoApp: true, pullPrimary: true }) : () => fetch({ refetchCoApp: true })}
              refreshAfterSave={noAutoRepull || ssnGuards ? refreshAfterSave : undefined}
              lastPull={noAutoRepull && debts.length === 0 ? lastPull : undefined}
              creditScore={creditScore}
              fileResultStatus={fileResultStatus}
              coAppFileResultStatus={coAppFileResultStatus}
              hasCoApplicant={hasCoApplicant && !!coApplicantId}
              onCreditSelectSaved={onCreditSelectSaved}
              debtSummary={debtSummary}
            />
          </div>
          :
          <div>
            <div className="bg-surface border border-divider shadow-lg rounded-2xl w-full max-w-[798px]">
              <div className="font-bold text-xl-20 pt-[24px] pl-[48px] pb-[16px]">
                Run Soft Credit Check
              </div>
              <div className="border-b border-divider mx-6" />
              <div>
                <div className="flex items-center justify-center gap-[10px] py-[24px]">
                  <div className="font-semibold text-[16px]">
                    Multi-Bureau Credit Check
                  </div>
                </div>
                <div className="bg-background mx-12 mb-6 rounded-2xl p-6">
                  You understand that by clicking on the "I Agree, Run Soft Credit Inquiry" button immediately following
                  this
                  notice, you are providing 'written instructions' to us under the Fair Credit Reporting Act
                  authorizing
                  us to obtain information from your personal credit report or other information from one or more
                  credit bureaus (including Equifax and Experian). You
                  authorize us to obtain such information solely to conduct a pre-qualification for credit.
                </div>
                <div className="pb-12 px-12 flex w-full gap-6">
                  <div className="w-full">
                    <Input
                      value={tempSsn}
                      label="Social Security Number"
                      placeholder="Enter ssn number"
                      onChange={(e) => handleInputChange(e.target.value)}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-6 mb-6 flex justify-end space-x-4">
              <Button
                width="min-w-[156px]"
                disabled={tempSsn.length <= 10}
                onClick={async () => {
                  const pullOnConsent = noAutoRepull && typedSsnDiffersFromKnown()
                  const success = await handleSsnNumber()
                  if (!success) {
                    return
                  }
                  const saved = await updateNewLeadCreditType({ type: 'softCreditCheck' })
                  if (!saved) {
                    return
                  }
                  setIsCreditCheck(true)
                  pullOnNextRefreshRef.current = pullOnConsent
                  setTriggerFetchDebts(prev => !prev)
                }}
              >
                I Agree, Run Soft Credit Check
              </Button>
              <Button
                width="min-w-[156px]"
                disabled={tempSsn.length <= 10}
                onClick={async () => {
                  const success = await handleSsnNumber()
                  if (!success) {
                    return
                  }
                  const saved = await updateNewLeadCreditType({ type: 'creditReportManually' })
                  if (!saved) {
                    return
                  }
                  setIsCreditCheck(false)
                  setTriggerFetchDebts(prev => !prev)
                }}
              >
                Add Credit Report Manually
              </Button>
            </div>
          </div>
      }
    </>
  )
}
